import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  Component,
  HostListener,
  Inject,
  OnInit,
  PLATFORM_ID,
  OnDestroy,
} from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { catchError, filter, of, Subject, takeUntil } from 'rxjs';
import { AuthService } from '../../core/auth/auth.service';
import { CommonService } from '../../core/common.service';
import { CONSTANTS } from '../../core/constants';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './header.component.html',
  styleUrl: './header.component.css',
})
export class HeaderComponent implements OnInit, OnDestroy {
  userObj: any;
  isLoginPage: boolean = false;
  isMinimalHeader: boolean = false;
  isCheckoutPage: boolean = false;
  isTabletScreen: boolean = false;
  baseImageUrl = CONSTANTS.baseImageUrl;
  isOpenSidebar: boolean = false;

  private destroy$ = new Subject<void>();

  constructor(
    public _authService: AuthService,
    private _router: Router,
    private _commonService: CommonService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    this._router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe((event: any) => {
        this.isLoginPage = event.urlAfterRedirects === '/login';
        this.isMinimalHeader =
          event.urlAfterRedirects.startsWith('/tutor-signup') ||
          event.urlAfterRedirects === '/thank-you';
        this.isCheckoutPage =
          event.urlAfterRedirects === '/bookings/checkout' ||
          event.urlAfterRedirects === '/bookings/success';
        this.userObj = this._commonService.getProfileDataFromLocalStorage();
      });
  }

  ngOnInit(): void {
    this.userObj = this._commonService.getProfileDataFromLocalStorage();
    this.isMinimalHeader =
      this._router.url.startsWith('/tutor-signup') ||
      this._router.url === '/thank-you';

    // Subscribe to reactive profile updates
    this._commonService.studentProfile$
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: any) => {
        if (data) {
          this.userObj = data;
        }
      });

    this._commonService.tutorProfile$
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: any) => {
        if (data) {
          this.userObj = data;
        }
      });

    this.checkScreenWidth();

    // Fetch fresh profile data
    const role = this._authService.getRole();
    if (role && this._authService.getToken()) {
      this._commonService
        .getProfile(role === 'student')
        .pipe(catchError(() => of(null)))
        .subscribe();
    }
  }

  get isLogin(): boolean {
    return !!this._authService.getToken();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    if (isPlatformBrowser(this.platformId)) {
      this.checkScreenWidth();
    }
  }

  checkScreenWidth() {
    if (isPlatformBrowser(this.platformId)) {
      this.isTabletScreen = window.innerWidth <= 1024;
    }
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent) {
    if (isPlatformBrowser(this.platformId)) {
      const dropdown = document.getElementById('profile-dropdown');
      const triggers = document.querySelectorAll(
        '[data-dropdown-trigger="profile"]',
      );

      let clickedInsideTrigger = false;
      triggers.forEach((trigger) => {
        if (trigger.contains(event.target as Node)) {
          clickedInsideTrigger = true;
        }
      });

      if (dropdown && dropdown.classList.contains('active')) {
        if (!dropdown.contains(event.target as Node) && !clickedInsideTrigger) {
          dropdown.classList.remove('active');
        }
      }
    }
  }

  logOut() {
    this._authService.signOut().subscribe(() => {
      this._commonService.clearCache();
      this.userObj = null;
      this._router.navigate(['/login']);
    });
  }

  toggleClass(id: string, className: string) {
    if (isPlatformBrowser(this.platformId)) {
      const element = document.getElementById(id);
      if (element) {
        element.classList.toggle(className);
      }
    }
  }

  removeClass(id: string, className: string) {
    if (isPlatformBrowser(this.platformId)) {
      const element = document.getElementById(id);
      if (element) {
        element.classList.remove(className);
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
